import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { supabase, projectRef } from "../lib/supabase";
import { ContextHeader } from "./ContextHeader";

// Global navigation is design note §10.1, verbatim: Workspace, Clients &
// engagements, Actions, Instrument library, Administration. Screens that are
// later releases still get a nav entry with a designed empty state (§11), so
// the shell's shape is honest about the product's shape from day one.
//
// The frame follows the LINNFOSS tool-universe shell as the Software
// Workbench builds it (its ledger D-20260921-020, src/shell/Layout.tsx):
// Steel Dark sidebar with the white wordmark and the product name beneath
// it, the person at the foot, and a thin top band with the context trail and
// the user menu, where sign-out lives.

const PRODUCT_DISPLAY_NAME = "Assessments";

const NAV = [
  { to: "/", label: "Workspace", end: true },
  { to: "/invite", label: "Invitations", end: false },
  { to: "/clients", label: "Clients & engagements", end: false },
  { to: "/actions", label: "Actions", end: false },
  { to: "/instruments", label: "Instrument library", end: false },
  { to: "/admin", label: "Administration", end: false },
] as const;

export function Layout({ userEmail }: { userEmail: string }) {
  return (
    <div className="app">
      <nav className="nav" aria-label="Main">
        <Link className="nav-brand" to="/">
          <img src="/brand/linnfoss-logo-light.svg" alt="LINNFOSS" />
          <span>{PRODUCT_DISPLAY_NAME}</span>
        </Link>

        <div className="nav-scroll">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}
            >
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>

        <div className="nav-foot">
          <div className="who" title={userEmail}>{userEmail}</div>
          <div title="The database this build talks to.">db: {projectRef}</div>
        </div>
      </nav>

      <div className="main">
        <ContextHeader>
          <UserMenu email={userEmail} />
        </ContextHeader>
        <main style={{ flex: 1, padding: "1.25rem 1.5rem", maxWidth: 1100 }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** Top-right user menu: where the tool-universe shell puts sign-out (Workbench UserMenu). */
function UserMenu({ email }: { email: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const initials = email.slice(0, 2).toUpperCase();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="user-menu no-print" ref={ref}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="avatar" aria-hidden="true">{initials}</span>
        <span>{email}</span>
      </button>
      {open && (
        <div role="menu" aria-label="Account">
          <button type="button" role="menuitem" onClick={() => supabase.auth.signOut()}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
